/**
 * Deadline filters for post-its.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { cn } from '@/lib/utils';
import { getSchoolYears } from '@/services/school-years.service';
import {
  POST_IT_PERIOD_OPTIONS,
  POST_IT_QUARTER_OPTIONS,
  type DeadlineFilter,
} from '@/components/post-its/post-it-configs';
import {
  getDeadlineRangeForQuarters,
  pickCurrentSchoolYearDateRange,
  resolveDeadlineFilterChange,
  toggleNumberInList,
} from '@/components/post-its/post-it-table-utils';

/** Props for {@link PersonalPostItsFilters}. */
interface PersonalPostItsFiltersProps {
  filter: DeadlineFilter;
  onFilterChange: (f: DeadlineFilter) => void;
  deadlineFrom: string;
  deadlineTo: string;
  onDeadlineFromChange: (date: string) => void;
  onDeadlineToChange: (date: string) => void;
  quarterlyPeriods?: number[];
  onQuarterlyPeriodsChange?: (p: number[]) => void;
}

/** Deadline filters for post-its. */
export const PersonalPostItsFilters: React.FC<PersonalPostItsFiltersProps> = ({
  filter,
  onFilterChange,
  deadlineFrom,
  deadlineTo,
  onDeadlineFromChange,
  onDeadlineToChange,
  quarterlyPeriods = [],
  onQuarterlyPeriodsChange,
}) => {
  const { data: schoolYearsRes } = useQuery({
    queryKey: ['school-years', 'current'],
    queryFn: () => getSchoolYears({ isCurrent: true, pageSize: 1 }),
  });

  const currentSchoolYear = React.useMemo(
    () => pickCurrentSchoolYearDateRange(schoolYearsRes),
    [schoolYearsRes],
  );

  const applyDeadlineRange = (range: { from: string; to: string } | null) => {
    onDeadlineFromChange(range?.from ?? '');
    onDeadlineToChange(range?.to ?? '');
  };

  const handleFilterChange = (next: DeadlineFilter) => {
    onFilterChange(next);
    const resolved = resolveDeadlineFilterChange(next, currentSchoolYear);
    onQuarterlyPeriodsChange?.(resolved.quarterlyPeriods);
    applyDeadlineRange(resolved.range);
  };

  const togglePeriod = (value: number) => {
    if (!onQuarterlyPeriodsChange) return;
    const next = toggleNumberInList(quarterlyPeriods, value);
    onQuarterlyPeriodsChange(next);
    applyDeadlineRange(getDeadlineRangeForQuarters(next, currentSchoolYear));
  };

  return (
    <div className="filter-groups-container mb-5">
      <div className="filter-group">
        <span className="filter-group-label">Période (deadline)</span>
        <SegmentedControl
          size="sm"
          value={filter}
          onChange={(v) => handleFilterChange(v as DeadlineFilter)}
          options={POST_IT_PERIOD_OPTIONS}
        />
      </div>

      {filter === 'quarter' && (
        <div className="filter-group">
          <span className="filter-group-label">Période(s)</span>
          <div className="inline-flex rounded-lg overflow-hidden border border-[var(--color-2-nuance-1)]">
            {POST_IT_QUARTER_OPTIONS.map((p, index) => {
              const isActive = quarterlyPeriods.includes(p.value);
              const isLast = index === POST_IT_QUARTER_OPTIONS.length - 1;
              return (
                <Button
                  key={p.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium transition-all border-0',
                    !isLast && 'border-r border-[var(--color-2-nuance-1)]',
                    isActive ? 'session-period-btn--active' : 'session-period-btn',
                  )}
                  onClick={() => togglePeriod(p.value)}
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {filter === 'specific_date' && (
        <div className="filter-group">
          <span className="filter-group-label">Date fin (échéance)</span>
          <div className="w-auto mt-0.5">
            <DateRangeInput
              startDate={deadlineFrom || undefined}
              endDate={deadlineTo || undefined}
              onStartDateChange={onDeadlineFromChange}
              onEndDateChange={onDeadlineToChange}
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};
